import type {
  DoorConfig,
  DrivewayConfig,
  FacadeSide,
  GarageConfig,
  HouseBodyConfig,
  LotConfig,
  StoryConfig,
} from '@/model/schema';
import type { Rng } from './rng';

/**
 * Attached-garage generator. Produces a `GarageConfig` describing a
 * single-story garage glued to one of the house's side facades, plus a
 * ground-floor exterior `DoorConfig` for the interior service door
 * that links the house to the garage, plus an optional `DrivewayConfig`
 * that runs from the lot's front edge up to the garage door.
 *
 * Two orientations are produced:
 *
 *  - **frontFacing** (preferred, ~70% chance when feasible): the
 *    garage is a wing of the house attached on its left or right side.
 *    The garage door is on the FRONT facade (same direction as the
 *    house's entry door, facing the street). This is the standard
 *    American suburban layout.
 *
 *  - **sideFacing** (fallback / variety, ~30% chance): the garage door
 *    is on the OUTWARD side facade — rear-loaded or alley-style.
 *    Geometry-wise this is the legacy implementation.
 *
 * Constraints enforced here:
 *  - Garage never crosses the lot edge (keeps a `LOT_EDGE_MARGIN` from
 *    every fence line so the attached structure looks reasonable).
 *  - Garage attaches to left / right (frontFacing) or left / right /
 *    back (sideFacing) — never to the front facade itself.
 *  - Garage width fits 1 or 2 cars plus 0.4 m clearance on each side.
 *  - Shared wall has room for a single 0.9 m service door flanked by
 *    0.3 m reveal on each side; we drop the interior door if there's
 *    no room.
 *  - For frontFacing variants we also reserve a driveway slab from the
 *    lot's front edge up to the garage door so the user actually has
 *    somewhere to roll the car onto.
 */

const SINGLE_CAR_WIDTH = 3.2;
const DOUBLE_CAR_WIDTH = 5.8;
/** Length of a parking stall — same dimension whether the door is on
 *  the short side (frontFacing) or one of the long sides (sideFacing). */
const GARAGE_LENGTH = 6.0;
const GARAGE_HEIGHT = 2.6;
/** Keep the garage this far from every fence / lot edge. */
const LOT_EDGE_MARGIN = 0.5;
/** Keep the garage this far from every house-body corner on the host facade. */
const HOUSE_CORNER_MARGIN = 0.8;
/** Interior service door width and corner reveal inside the shared span. */
const INT_DOOR_WIDTH = 0.9;
const INT_DOOR_REVEAL = 0.3;
/** Driveway is slightly wider than the garage door for car clearance. */
const DRIVEWAY_OVERWIDTH = 0.6;

export interface GarageResult {
  garage: GarageConfig;
  /** Interior service door to drop into stories[0].doors (role 'garage'). */
  interiorDoor: DoorConfig;
  /** Driveway slab to add to the config's top-level `driveway` field.
   *  Only present for frontFacing garages — sideFacing garages would
   *  need an alley-style approach we don't model yet. */
  driveway?: DrivewayConfig;
}

export function generateGarage(
  rng: Rng,
  body: HouseBodyConfig,
  lot: LotConfig,
  story0: StoryConfig,
): GarageResult | null {
  void story0;

  // Try frontFacing first if there's room on either side. Real American
  // suburban layouts favour this orientation, and the user explicitly
  // asked for it as the preferred case.
  const frontFacing = tryFrontFacing(rng, body, lot);
  if (frontFacing && rng.chance(0.7)) return frontFacing;
  const sideFacing = trySideFacing(rng, body, lot);
  return sideFacing ?? frontFacing;
}

/* ---------------------- frontFacing implementation -------------------- */

function tryFrontFacing(
  rng: Rng,
  body: HouseBodyConfig,
  lot: LotConfig,
): GarageResult | null {
  // Side wing on the house's left or right. Need lateral lot space for
  // the garage's lateral extent (== car width) AND front-to-back lot
  // space for the driveway run from the lot front edge to the garage
  // door. The garage shares a wall along the body's side facade, with
  // its FRONT face flush (recess=0) or slightly behind (recess>0) the
  // body's front facade.
  const halfLotW = lot.width / 2;
  const halfLotD = lot.depth / 2;
  const bodyLeftWorld = body.originX;
  const bodyRightWorld = body.originX + body.footprintWidth;
  const bodyFrontWorld = body.originZ; // small Z = front
  const bodyBackWorld = body.originZ + body.footprintDepth;
  const leftLotGap = bodyLeftWorld - (-halfLotW);
  const rightLotGap = halfLotW - bodyRightWorld;

  const cars: GarageConfig['cars'] = rng.chance(0.4) ? 'double' : 'single';
  const width = cars === 'double' ? DOUBLE_CAR_WIDTH : SINGLE_CAR_WIDTH;

  type Cand = { side: 'left' | 'right'; lateralGap: number };
  const cands: Cand[] = [];
  if (leftLotGap >= width + LOT_EDGE_MARGIN) cands.push({ side: 'left', lateralGap: leftLotGap });
  if (rightLotGap >= width + LOT_EDGE_MARGIN) cands.push({ side: 'right', lateralGap: rightLotGap });
  if (cands.length === 0) return null;

  // Need front clearance for the driveway too (at least a car length).
  const frontLotGap = bodyFrontWorld - (-halfLotD);
  if (frontLotGap < 1.5) return null;
  void bodyBackWorld;

  const picked = cands[rng.int(0, cands.length - 1)]!;
  // The garage extends along Z from `recess` to `recess + GARAGE_LENGTH`.
  // We require it stays inside the body's depth so the back of the
  // garage doesn't poke past the body's back face — when it would, we
  // truncate.
  const maxRecess = Math.max(0, body.footprintDepth - GARAGE_LENGTH);
  const recess = rng.chance(0.55) ? 0 : Math.min(maxRecess, rng.range(0.6, 2.0));
  const garageDepth = Math.min(GARAGE_LENGTH, body.footprintDepth - recess);
  if (garageDepth < SINGLE_CAR_WIDTH) return null;

  // The renderer's local-X axis on each side starts at that facade's
  // "lower-index corner": FRONT for the right facade, BACK for the
  // left facade (mirrored). We want the garage glued to the FRONT
  // regardless of side, so we convert `recess` (always measured from
  // the front line) into the facade-local offset accordingly.
  const offsetAlongHost =
    picked.side === 'right'
      ? recess
      : body.footprintDepth - recess - garageDepth;

  const hasInteriorDoor = garageDepth >= INT_DOOR_WIDTH + 2 * INT_DOOR_REVEAL;
  // The interior door lives along the shared wall — i.e. it's an
  // exterior door on the body's left/right facade, with offset
  // measured along that facade from the lower-corner. Place it near
  // the BACK of the shared span so users walk past the parked car
  // toward the back of the garage and into the house — typical
  // US layout where the laundry/mudroom is at the back.
  // The shared span runs from `recess` (front of garage) to
  // `recess + garageDepth` (back of garage). On the host facade,
  // offsets are measured from the lower-index corner, which is the
  // FRONT corner for left/right facades — so offset increases as we
  // move toward the back.
  const interiorDoorOffset = hasInteriorDoor
    ? Math.max(INT_DOOR_REVEAL, garageDepth - INT_DOOR_WIDTH - INT_DOOR_REVEAL)
    : 0;

  const doorType: GarageConfig['doorType'] = rng.chance(0.85) ? 'rollUp' : 'swingUp';

  const garage: GarageConfig = {
    side: picked.side,
    offset: offsetAlongHost,
    width,
    depth: garageDepth,
    height: GARAGE_HEIGHT,
    cars,
    doorType,
    orientation: 'frontFacing',
    frontRecess: recess,
    hasInteriorDoor,
    interiorDoorOffset,
    interiorDoorWidth: INT_DOOR_WIDTH,
  };

  // Compute the world position of the garage door so the driveway runs
  // from there to the lot's front edge. For frontFacing, the door is
  // centered on the garage's front face (which is at world Z =
  // bodyFrontWorld + recess) and centered laterally on the garage's
  // X span.
  const garageFrontZWorld = bodyFrontWorld + recess;
  const garageCenterXWorld =
    picked.side === 'left'
      ? bodyLeftWorld - width / 2
      : bodyRightWorld + width / 2;
  const drvHalfW = (width + DRIVEWAY_OVERWIDTH) / 2;
  const driveway: DrivewayConfig = {
    side: 'front',
    xLo: garageCenterXWorld - drvHalfW,
    xHi: garageCenterXWorld + drvHalfW,
    zLo: -halfLotD,
    zHi: garageFrontZWorld,
    surface: rng.chance(0.7) ? 'concrete' : 'asphalt',
  };

  // Service door is on the host facade (body's left or right facade)
  // and uses the same lower-index-corner offset convention. The door
  // sits inside the shared span; we already computed the interior
  // door's offset INSIDE the garage's local +X span, but the host
  // wall's offset convention may run in the opposite direction. Pin
  // the door near the BACK end of the shared span (away from the
  // street so the user walks past the parked car to enter the house).
  const interiorDoorHostOffset =
    picked.side === 'right'
      ? offsetAlongHost + interiorDoorOffset
      : offsetAlongHost + (garageDepth - interiorDoorOffset - INT_DOOR_WIDTH);
  const interiorDoor: DoorConfig = {
    id: `D-garage-${picked.side}`,
    side: picked.side,
    offset: interiorDoorHostOffset,
    width: INT_DOOR_WIDTH,
    height: 2.05,
    role: 'garage',
  };

  return { garage, interiorDoor, driveway };
}

/* ---------------------- sideFacing (legacy) --------------------------- */

function trySideFacing(
  rng: Rng,
  body: HouseBodyConfig,
  lot: LotConfig,
): GarageResult | null {
  const candidates: { side: FacadeSide; hostLen: number; outward: number }[] = [];
  const halfLotW = lot.width / 2;
  const halfLotD = lot.depth / 2;
  const bodyHalfW = body.footprintWidth / 2;
  const bodyHalfD = body.footprintDepth / 2;
  const bodyCx = body.originX + bodyHalfW;
  const bodyCz = body.originZ + bodyHalfD;
  const leftGap = bodyCx - bodyHalfW + halfLotW;
  const rightGap = halfLotW - (bodyCx + bodyHalfW);
  const backGap = halfLotD - (bodyCz + bodyHalfD);

  const minOutward = GARAGE_LENGTH + LOT_EDGE_MARGIN;
  if (leftGap >= minOutward) {
    candidates.push({ side: 'left', hostLen: body.footprintDepth, outward: leftGap });
  }
  if (rightGap >= minOutward) {
    candidates.push({ side: 'right', hostLen: body.footprintDepth, outward: rightGap });
  }
  if (backGap >= minOutward) {
    candidates.push({ side: 'back', hostLen: body.footprintWidth, outward: backGap });
  }
  if (candidates.length === 0) return null;

  const picked = candidates[rng.int(0, candidates.length - 1)]!;
  const cars: GarageConfig['cars'] =
    picked.hostLen - 2 * HOUSE_CORNER_MARGIN >= DOUBLE_CAR_WIDTH && rng.chance(0.5)
      ? 'double'
      : 'single';
  const width = cars === 'double' ? DOUBLE_CAR_WIDTH : SINGLE_CAR_WIDTH;
  if (picked.hostLen - 2 * HOUSE_CORNER_MARGIN < width) return null;

  const minOffset = HOUSE_CORNER_MARGIN;
  const maxOffset = picked.hostLen - HOUSE_CORNER_MARGIN - width;
  const offset = rng.range(minOffset, maxOffset);
  const doorType: GarageConfig['doorType'] = rng.chance(0.7) ? 'rollUp' : 'swingUp';
  const hasInteriorDoor = width >= INT_DOOR_WIDTH + 2 * INT_DOOR_REVEAL;
  const interiorDoorOffset = hasInteriorDoor ? (width - INT_DOOR_WIDTH) / 2 : 0;

  const garage: GarageConfig = {
    side: picked.side,
    offset,
    width,
    depth: GARAGE_LENGTH,
    height: GARAGE_HEIGHT,
    cars,
    doorType,
    orientation: 'sideFacing',
    frontRecess: 0,
    hasInteriorDoor,
    interiorDoorOffset,
    interiorDoorWidth: INT_DOOR_WIDTH,
  };

  const interiorDoor: DoorConfig = {
    id: `D-garage-${picked.side}`,
    side: picked.side,
    offset: offset + interiorDoorOffset,
    width: INT_DOOR_WIDTH,
    height: 2.05,
    role: 'garage',
  };

  // Driveway for the sideways garage. The car drives in from the
  // outward fence side perpendicular to the host facade — left/right/back
  // are mapped to the matching lot edge. Width is parallel to the
  // garage door (along the host facade), expanded by `DRIVEWAY_OVERWIDTH`
  // for car clearance.
  const drvOver = DRIVEWAY_OVERWIDTH;
  const halfWidthAlongFacade = (width + drvOver) / 2;
  let driveway: DrivewayConfig | undefined;
  if (picked.side === 'left' || picked.side === 'right') {
    // The garage occupies the body's left/right side. The host axis is
    // Z (front-to-back). On the right facade local-X starts at the front
    // (bodyFrontZ); on the left facade it starts at the back. The
    // garage's lateral world span (Z) is therefore mapped accordingly.
    const garageZLo =
      picked.side === 'right'
        ? body.originZ + offset
        : body.originZ + body.footprintDepth - offset - width;
    const garageZHi = garageZLo + width;
    const padCenter = (garageZLo + garageZHi) / 2;
    const drvZLo = padCenter - halfWidthAlongFacade;
    const drvZHi = padCenter + halfWidthAlongFacade;
    if (picked.side === 'left') {
      const garageDoorX = body.originX - GARAGE_LENGTH;
      driveway = {
        side: 'left',
        xLo: -halfLotW,
        xHi: garageDoorX,
        zLo: drvZLo,
        zHi: drvZHi,
        surface: rng.chance(0.7) ? 'concrete' : 'asphalt',
      };
    } else {
      const garageDoorX = body.originX + body.footprintWidth + GARAGE_LENGTH;
      driveway = {
        side: 'right',
        xLo: garageDoorX,
        xHi: halfLotW,
        zLo: drvZLo,
        zHi: drvZHi,
        surface: rng.chance(0.7) ? 'concrete' : 'asphalt',
      };
    }
  } else if (picked.side === 'back') {
    // Garage attached to the back facade; door faces +Z (lot back).
    // Lateral extent runs along X.
    const garageXLo = body.originX + offset;
    const garageXHi = garageXLo + width;
    const padCenter = (garageXLo + garageXHi) / 2;
    const drvXLo = padCenter - halfWidthAlongFacade;
    const drvXHi = padCenter + halfWidthAlongFacade;
    const garageDoorZ = body.originZ + body.footprintDepth + GARAGE_LENGTH;
    driveway = {
      side: 'back',
      xLo: drvXLo,
      xHi: drvXHi,
      zLo: garageDoorZ,
      zHi: halfLotD,
      surface: rng.chance(0.7) ? 'concrete' : 'asphalt',
    };
  }

  return { garage, interiorDoor, driveway };
}
